import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Quote, Star } from 'lucide-react';
import { motion } from 'framer-motion';

export const Testimonials = () => {
  const { language } = useLanguage();
  const fr = language === 'fr';

  const stats = [
    { value: '2008', label: fr ? 'Année de fondation' : 'Founded in' },
    { value: '-70%', label: fr ? 'de temps administratif' : 'admin time saved' },
    { value: '0', label: fr ? 'double réservation constatée' : 'double bookings recorded' },
    { value: '18%', label: fr ? 'de km non facturés récupérés' : 'unbilled km recovered' },
  ];

  const testimonials = fr ? [
    {
      text: "Nous avons divisé par trois le temps passé sur l'administratif. Les contrats se génèrent automatiquement et nos clients réservent en ligne, même le week-end.",
      author: 'Marc D.',
      role: 'Gérant, Garage du Léman — Genève',
    },
    {
      text: "Le suivi GPS avec Logitrak nous a permis de réduire les kilomètres non facturés de 18 %. Le tableau de bord est d'une clarté rare.",
      author: 'Sophie R.',
      role: 'Directrice, AutoLoc Riviera — Montreux',
    },
    {
      text: "Passer d'Excel à Logirent a été la meilleure décision de l'année : plus aucune double réservation depuis huit mois.",
      author: 'Luca B.',
      role: 'Responsable de flotte, MobiliCar SA — Lausanne',
    },
  ] : [
    {
      text: 'We cut our admin time by two thirds. Contracts are generated automatically and our customers book online, even on weekends.',
      author: 'Marc D.',
      role: 'Manager, Garage du Léman — Geneva',
    },
    {
      text: 'GPS tracking with Logitrak helped us reduce unbilled kilometres by 18%. The dashboard is remarkably clear.',
      author: 'Sophie R.',
      role: 'Director, AutoLoc Riviera — Montreux',
    },
    {
      text: 'Moving from Excel to Logirent was the best decision of the year: not a single double booking in eight months.',
      author: 'Luca B.',
      role: 'Fleet Manager, MobiliCar SA — Lausanne',
    },
  ];

  return (
    <section id="testimonials" className="py-16 md:py-20 bg-white" data-testid="testimonials-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-3 tracking-tight" data-testid="testimonials-title">
            {fr ? 'Ils gèrent leur flotte avec Logirent' : 'They manage their fleet with Logirent'}
          </h2>
          <p className="text-base text-slate-600 max-w-2xl mx-auto">
            {fr
              ? 'Des loueurs et gestionnaires de flotte en Suisse romande nous font confiance au quotidien.'
              : 'Rental companies and fleet managers across French-speaking Switzerland trust us every day.'}
          </p>
        </motion.div>

        {/* Stats bar */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12"
          data-testid="testimonials-stats"
        >
          {stats.map((s, i) => (
            <div key={i} className="bg-slate-50 rounded-xl border border-slate-100 p-5 text-center">
              <div className="text-2xl md:text-3xl font-bold text-blue-600 mb-1">{s.value}</div>
              <div className="text-xs md:text-sm text-slate-500">{s.label}</div>
            </div>
          ))}
        </motion.div>

        {/* Testimonial cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {testimonials.map((tm, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1, duration: 0.5 }}
              className="bg-slate-50 rounded-2xl border border-slate-100 p-6 flex flex-col"
              data-testid={`testimonial-card-${index}`}
            >
              <Quote className="w-8 h-8 text-blue-200 mb-4" />
              <p className="text-slate-700 leading-relaxed flex-1">{tm.text}</p>
              <div className="mt-6 pt-4 border-t border-slate-200">
                <div className="flex items-center gap-1 mb-2">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 text-amber-400 fill-amber-400" />
                  ))}
                </div>
                <p className="font-semibold text-slate-900 text-sm">{tm.author}</p>
                <p className="text-slate-500 text-xs">{tm.role}</p>
              </div>
            </motion.div>
          ))}
        </div>

        <p className="text-center text-xs text-slate-400 mt-8">
          {fr
            ? 'Chiffres constatés chez nos clients pilotes en Suisse romande (2025-2026).'
            : 'Figures observed among our pilot customers in French-speaking Switzerland (2025-2026).'}
        </p>
      </div>
    </section>
  );
};
